import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { Popover, useMediaQuery, useTheme } from '@mui/material';
import { NotificationPanel } from './NotificationPanel';
import { NotificationSheet } from './NotificationSheet';

interface NotificationPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

/**
 * The bell's panel: anchored under the bell with no scrim on larger screens,
 * a bottom sheet on phones. The panel only mounts while open.
 */
export const NotificationPopover: React.FC<NotificationPopoverProps> = ({ anchorEl, open, onClose }) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  // On phones, going back is an edge swipe (Safari) or the system back
  // gesture (Android), either of which can land while the sheet is open.
  // Close the sheet with the page it was opened over. Only for back and
  // forward: pages also rewrite their own URL (dropping ?highlight=, say),
  // which must not shut a sheet that was just opened.
  const { key } = useLocation();
  const navigationType = useNavigationType();
  const lastKey = useRef(key);
  useEffect(() => {
    if (lastKey.current === key) return;
    lastKey.current = key;
    if (isPhone && open && navigationType === 'POP') onClose();
  }, [key, navigationType, isPhone, open, onClose]);

  if (isPhone) {
    return (
      <NotificationSheet open={open} onClose={onClose}>
        <NotificationPanel onClose={onClose} mobile />
      </NotificationSheet>
    );
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        role: 'dialog',
        'aria-label': 'Notifications',
        sx: {
          mt: 1,
          width: 400,
          maxWidth: 'calc(100vw - 24px)',
          maxHeight: 'min(600px, calc(100vh - 88px))',
          display: 'flex',
          flexDirection: 'column',
          border: 1,
          borderColor: 'divider',
          boxShadow: '0 18px 44px -10px rgba(15, 23, 42, 0.24), 0 2px 6px rgba(15, 23, 42, 0.06)',
        },
      }}
    >
      <NotificationPanel onClose={onClose} />
    </Popover>
  );
};

export default NotificationPopover;
